/**
 * Google Maps Geocoding utility.
 *
 * Lazy-loads the Google Maps JavaScript API on first use, then provides a
 * `googleGeocode(address)` function that returns the top result with
 * lat/lng, viewport (for smart zoom), and formatted address.
 */
import { GOOGLE_API_KEY } from '../config/google';

/* ── Typings (minimal, avoids @types/google.maps dependency) ── */

interface LatLng {
  lat(): number;
  lng(): number;
}

interface LatLngBounds {
  getNorthEast(): LatLng;
  getSouthWest(): LatLng;
}

interface GeocoderGeometry {
  location: LatLng;
  viewport?: LatLngBounds;
  location_type: string;
}

interface GeocoderResult {
  formatted_address: string;
  geometry: GeocoderGeometry;
  types: string[];
}

interface Geocoder {
  geocode(
    request: { address: string },
    callback: (results: GeocoderResult[] | null, status: string) => void,
  ): void;
}

/* ── Loader ── */

let loadPromise: Promise<void> | null = null;

function loadGoogleMapsApi(): Promise<void> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    // Already loaded (e.g. StreetView triggered it earlier)
    if ((window as any).google?.maps?.Geocoder) {
      resolve();
      return;
    }

    // Callback-based loader (avoids duplicate script tags)
    const cbName = '__gm_init_' + Date.now();
    (window as any)[cbName] = () => {
      delete (window as any)[cbName];
      resolve();
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=geocoding&callback=${cbName}`;
    script.async = true;
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Google Maps JS API'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

/* ── Public API ── */

export interface GeocodeResult {
  lat: number;
  lng: number;
  formattedAddress: string;
  /** Viewport bounds — use for view.fit() to get perfect zoom */
  viewport: {
    south: number;
    north: number;
    west: number;
    east: number;
  } | null;
  locationType: string;
  types: string[];
}

/**
 * Geocode an address string via the Google Maps JavaScript API.
 * Returns the top result, or null if nothing found / API error.
 */
export async function googleGeocode(address: string): Promise<GeocodeResult | null> {
  try {
    await loadGoogleMapsApi();

    const geocoder: Geocoder = new (window as any).google.maps.Geocoder();

    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status !== 'OK' || !results || results.length === 0) {
          console.warn('[GoogleGeocode] status:', status);
          resolve(null);
          return;
        }

        const top = results[0];
        const loc = top.geometry.location;
        const vp = top.geometry.viewport;

        resolve({
          lat: loc.lat(),
          lng: loc.lng(),
          formattedAddress: top.formatted_address,
          viewport: vp
            ? {
                south: vp.getSouthWest().lat(),
                north: vp.getNorthEast().lat(),
                west: vp.getSouthWest().lng(),
                east: vp.getNorthEast().lng(),
              }
            : null,
          locationType: top.geometry.location_type,
          types: top.types,
        });
      });
    });
  } catch (e) {
    console.warn('[GoogleGeocode] Error:', e);
    return null;
  }
}
